// Shape helpers for the sidebar nav tree.
//
// The server-filtered uiConfig is the structure (see ui_configurations in Mongo):
// `type: "section"` items are non-navigable captions, items with children are
// containers, items with a `route` are destinations. A container can be both —
// e.g. Shopify Product Manager has a hub page AND holds the tools.

// How many levels of rows the rail renders. Anything nested deeper is reached
// from its parent's hub page instead (Map Update's four MAP tools, today), which
// is what keeps the rail shallow while the document still describes the real
// containment.
export const NAV_MAX_DEPTH = 3;

export const isNavSection = (item) => item?.type === "section";

const routePath = (route) => (route || "").split("?")[0];

/**
 * Drops captions and containers that ended up with nothing in them — the server
 * keeps a parent whose own rule passes even when every child was filtered out
 * (CapabilitiesService.filterItems), and sections have no rule at all, so an
 * Ecom user would otherwise see an empty "Admin" caption.
 */
export const pruneNavTree = (items = []) =>
  items.reduce((kept, item) => {
    const children = pruneNavTree(item.children || []);
    const navigable = Boolean(item.route);
    if (!navigable && children.length === 0) return kept;
    kept.push({ ...item, children });
    return kept;
  }, []);

/**
 * Every navigable item in the tree, in document order, each carrying the labels
 * and ids of its ancestors. Powers the nav filter, pinned/recent lookups and
 * the global search.
 */
export const flattenNavLeaves = (items = [], trail = []) =>
  items.flatMap((item) => {
    const ownTrail = [...trail, item];
    const self = item.route
      ? [{
          ...item,
          trailIds: trail.map((ancestor) => ancestor.id),
          trailLabels: trail
            .filter((ancestor) => !isNavSection(ancestor))
            .map((ancestor) => ancestor.label),
        }]
      : [];
    return [...self, ...flattenNavLeaves(item.children || [], ownTrail)];
  });

/**
 * Ids from the root down to the item the current path is on, so the rail can
 * highlight the row and auto-expand its ancestors. Prefers an exact route match
 * and falls back to the longest route the path sits under, so a nested or
 * query-carrying URL still lights up its tool.
 */
export const findActiveTrail = (items = [], currentPath = "") => {
  const leaves = flattenNavLeaves(items);
  const pathOnly = routePath(currentPath);

  let match =
    leaves.find((leaf) => leaf.route === currentPath) ||
    leaves.find(
      (leaf) => !leaf.route.includes("?") && routePath(leaf.route) === pathOnly
    );

  if (!match) {
    match = leaves
      .filter((leaf) => {
        const base = routePath(leaf.route);
        return base !== "/" && pathOnly.startsWith(`${base}/`);
      })
      .sort((left, right) => routePath(right.route).length - routePath(left.route).length)[0];
  }

  return match ? [...match.trailIds, match.id] : [];
};

/** Case-insensitive match on label + description, for the nav filter box. */
export const matchesNavQuery = (leaf, query) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    (leaf.label || "").toLowerCase().includes(needle) ||
    (leaf.description || "").toLowerCase().includes(needle) ||
    leaf.trailLabels.some((label) => (label || "").toLowerCase().includes(needle))
  );
};
